import React from 'react';

import { SalesOverviewAnalytics } from 'common/analytics';
import Wahanda from 'common/wahanda';
import { Currency } from 'components/common/DataFormat';

import { dataCell, rightAlignedHeader } from '../common/reportTableCells';
import ProductHashLink from '../ProductSalesReport/hashLink';
import ServiceHashLink from '../ServiceSalesReport/hashLink';
import { SharedProps } from 'reconnect-types/moduleFederation';

export type SalesMetrics = {
  type?: string;
  paidAtVenue?: number;
  prePaid?: number;
  grossTotalSold?: number;
};

type DateLike = {
  formatApiDateString: () => string;
};

export type SalesReportData = {
  rows: SalesMetrics[];
  total: SalesMetrics;
};

const emptySalesMetrics: SalesMetrics = {
  paidAtVenue: 0,
  prePaid: 0,
  grossTotalSold: 0,
};

const findRowByType = (rows: SalesMetrics[], type: string): SalesMetrics =>
  rows.find((row) => row.type === type) ?? emptySalesMetrics;

export type SalesReportTableProps = {
  venueId: string | number;
  fromDate: DateLike;
  toDate: DateLike;
  selectedEmployeeId: number;
  selectedChannel: string;
  reportData: SalesReportData;
};

const salesOverviewDataCellTestIds = {
  paidAtVenue: 'sales-overview-paid-at-venue',
  prePaid: 'sales-overview-prepaid',
  total: 'sales-overview-total',
};

const buildMetricCells = (row: SalesMetrics) => [
  dataCell(<Currency amount={row.paidAtVenue} />, {
    dataTestId: salesOverviewDataCellTestIds.paidAtVenue,
  }),
  dataCell(<Currency amount={row.prePaid} />, {
    dataTestId: salesOverviewDataCellTestIds.prePaid,
  }),
  dataCell(<Currency amount={row.grossTotalSold} />, {
    variant: 'grandTotal',
    dataTestId: salesOverviewDataCellTestIds.total,
  }),
];

export const getSalesReportTableProps = ({
  venueId,
  fromDate,
  toDate,
  selectedEmployeeId,
  selectedChannel,
  reportData,
}: SalesReportTableProps): SharedProps['ReportTable'] => {
  const headings = Wahanda.lang.reports.sales.salesReport.headings;
  const rowLabels = Wahanda.lang.reports.sales.salesReport.rows;
  const { rows, total } = reportData;
  const serviceRow = findRowByType(rows, 'Services');
  const productRow = findRowByType(rows, 'Products');
  const voucherRow = findRowByType(rows, 'Vouchers');

  const linkProps = {
    venueId,
    fromDate,
    toDate,
    employeeFilter: selectedEmployeeId,
  };

  const headerRows = [
    [
      { content: '\u00a0', width: 'wide' as const, height: 'double' as const },
      {
        content: headings.salesIncludingVat,
        colSpan: 3,
        align: 'center' as const,
        height: 'stacked' as const,
      },
    ],
    [
      { content: '\u00a0' },
      rightAlignedHeader(headings.payAtVenue),
      rightAlignedHeader(headings.prePaid),
      rightAlignedHeader(headings.total),
    ],
  ];

  const tableRows = [
    {
      id: 'services',
      dataTestId: 'sales-overview-row',
      cells: [
        {
          content: (
            <ServiceHashLink
              {...linkProps}
              onClick={SalesOverviewAnalytics.trackNavigateToServiceClick}
              selectedChannel={selectedChannel}
            >
              {rowLabels.services}
            </ServiceHashLink>
          ),
        },
        ...buildMetricCells(serviceRow),
      ],
    },
    {
      id: 'products',
      dataTestId: 'sales-overview-row',
      cells: [
        {
          content: (
            <ProductHashLink
              {...linkProps}
              onClick={SalesOverviewAnalytics.trackNavigateToProductsClick}
            >
              {rowLabels.products}
            </ProductHashLink>
          ),
        },
        ...buildMetricCells(productRow),
      ],
    },
    {
      id: 'vouchers',
      dataTestId: 'sales-overview-row',
      cells: [{ content: rowLabels.vouchers }, ...buildMetricCells(voucherRow)],
    },
  ];

  const footerRow = {
    cells: [
      { content: headings.grandTotal },
      ...buildMetricCells(total).map((cell) => ({
        ...cell,
        variant: 'grandTotal' as const,
      })),
    ],
  };

  return {
    headerRows,
    rows: tableRows,
    footerRow,
    footerDataTestId: 'sales-overview-totals',
    testID: 'sales-overview-report-table',
  };
};
